import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, ElementRef, type OnDestroy, type OnInit, ViewChild } from '@angular/core';
import { type FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { combineLatest, type Observable, Subscription, withLatestFrom } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

import type { TranslatedCountryCode } from '@fcom/common-booking/components/pax-details/pax-details-form-group.component';
import { GtmService } from '@fcom/common/gtm';
import type { SelectOption } from '@fcom/common/interfaces';
import type { CorporateContactUs } from '@fcom/common/interfaces/corporate/corporate-contact-us';
import type { CorporateContactForm } from '@fcom/common/interfaces/corporate/corporate.interface';
import { ScrollService } from '@fcom/common/services';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import type { Profile } from '@fcom/core-api/login';
import type { AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { SentryLogger } from '@fcom/core/services';
import { EMAIL_REGEX, NAME_PATTERN, sortBy, unsubscribe } from '@fcom/core/utils';
import { parsePhoneNumber } from '@fcom/corporate/utils/utils';
import { finShare } from '@fcom/rx';
import { AnalyticsFormStatus, FormStatus, trackFormState } from '@fcom/service-forms';
import {
  type AttachmentResponse,
  ButtonTheme,
  ButtonType,
  MAX_ATTACHMENTS_10,
  NotificationLayout,
  NotificationTheme,
  type RadioItemGroupOption,
  TextInputType,
} from '@fcom/ui-components';
import { FileUploaderComponent } from '@fcom/ui-components/components/forms/file-uploader/file-uploader.component';
import { type Attachment, AttachmentStatus } from '@fcom/ui-components/components/interfaces';
import { LanguageService } from '@fcom/ui-translate';

const CORPORATE_CONTACT_FORM_NAME = 'corporate-contact-form';

@Component({
  selector: 'fin-corporate-contact-form',
  templateUrl: './corporate-contact-form.component.html',
  styleUrls: ['./corporate-contact-form.component.scss'],
})
export class CorporateContactFormComponent implements OnInit, OnDestroy {
  readonly TextInputType = TextInputType;
  readonly FormStatus = FormStatus;
  readonly ButtonType = ButtonType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly ButtonTheme = ButtonTheme;

  readonly allowedFileTypes: FileUploaderComponent['allowedFileTypes'] = [
    'bmp',
    'doc',
    'docx',
    'gif',
    'jpeg',
    'jpg',
    'pdf',
    'png',
    'txt',
    'xls',
    'xlsx',
  ];
  readonly maxAttachments: FileUploaderComponent['maxAttachments'] = MAX_ATTACHMENTS_10;
  readonly THREE_MEGABYTES = 3145728;

  @ViewChild('thankYouTitle')
  thankYouTitle: ElementRef;
  @ViewChild('contactDetailsTitle')
  contactDetailsTitle: ElementRef;
  @ViewChild('fileUploader')
  fileUploader: FileUploaderComponent;

  reactiveForm: FormGroup<CorporateContactForm>;
  formStatus: FormStatus = FormStatus.INITIAL;

  topics: RadioItemGroupOption[] = [
    { value: 'additionalInfo' },
    { value: 'changeInformation' },
    { value: 'technicalIssue' },
    { value: 'feedback' },
  ];
  reasonMap = {
    additionalInfo: 'Account or User Information',
    changeInformation: 'Travel Agent Information',
    technicalIssue: 'Technical Issue',
    feedback: 'Feedback',
  };
  subjectMap = {
    additionalInfo: 'Request an additional user to be added / delete an existing user / change company information',
    changeInformation: 'Change travel agent information in your current agreement',
    technicalIssue: 'Report a technical issue',
    feedback: 'Give feedback on the Finnair Corporate Programme',
  };
  countryCodes$: Observable<TranslatedCountryCode[]>;
  selectedCountryCode: TranslatedCountryCode;

  returnLink$: Observable<string>;
  countries$: Observable<SelectOption[]>;
  salutations$: Observable<SelectOption[]>;
  phonePrefixes$: Observable<SelectOption[]>;
  profile$: Observable<Profile>;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private elementRef: ElementRef,
    private fb: UntypedFormBuilder,
    private gtmService: GtmService,
    private languageService: LanguageService,
    private scrollService: ScrollService,
    private store$: Store<AppState>,
    public corporateService: CorporateService,
    public sentryLogger: SentryLogger
  ) {}

  ngOnInit(): void {
    this.profile$ = this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), startWith(undefined), finShare());
    this.returnLink$ = this.corporateService.getReturnLink();
    this.countryCodes$ = this.languageService.translate('countryCodes').pipe(finShare());
    this.setInputOptions();
    this.prefill();
  }

  countryChanged(countryCode: string, countryCodes: TranslatedCountryCode[]): void {
    this.selectedCountryCode = countryCodes?.find((cc) => cc.countryCode === countryCode);
    this.reactiveForm
      .get('phonePrefix')
      .setValue(`${this.selectedCountryCode?.countryCode}|${this.selectedCountryCode?.phonePrefix}`);
  }

  handleError(err: unknown): void {
    if (err instanceof HttpErrorResponse && err.status === HttpStatusCode.PreconditionRequired) {
      this.formStatus = FormStatus.PRECONDITION_REQUIRED;
      trackFormState(CORPORATE_CONTACT_FORM_NAME, AnalyticsFormStatus.PRECONDITION_REQUIRED, this.gtmService);
      this.sentryLogger.warn('Corporate contact form Akamai Crypto Challenge', {
        err,
      });
    } else {
      this.formStatus = FormStatus.ERROR;
      trackFormState(CORPORATE_CONTACT_FORM_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
      this.sentryLogger.error('Failed to post contact request to corporate api', {
        err,
      });
    }
  }

  onSubmit(): void {
    if (!this.reactiveForm.valid) {
      this.corporateService.scrollToInvalidField(this.reactiveForm, this.elementRef);
      return;
    }
    this.formStatus = FormStatus.LOADING;
    trackFormState(CORPORATE_CONTACT_FORM_NAME, AnalyticsFormStatus.SEND_ATTEMPT, this.gtmService);
    this.subscriptions.add(
      combineLatest([this.getPostPayload(), this.getPostPayload(true), this.profile$])
        .pipe(
          switchMap(([sfPayload, emailPayload, profile]) => {
            return combineLatest([
              this.corporateService.sendContactCaseToSalesForce(sfPayload, profile),
              this.corporateService.postContactRequest(emailPayload),
            ]);
          })
        )
        .subscribe({
          next: () => {
            this.formStatus = FormStatus.COMPLETE;
            trackFormState(CORPORATE_CONTACT_FORM_NAME, AnalyticsFormStatus.SUCCESS, this.gtmService);
            setTimeout(() => {
              this.scrollService.smoothScroll(this.thankYouTitle);
            });
          },
          error: (err) => {
            this.handleError(err);
          },
        })
    );
  }

  stripPhoneNumber(phoneNumber: string): string {
    return phoneNumber?.replace(`+${this.selectedCountryCode.phonePrefix}`, '') || '';
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  uploadFile(): (f: File) => Observable<AttachmentResponse> {
    return (file: File): Observable<AttachmentResponse> => {
      return this.corporateService.postFile(file);
    };
  }

  private getRequiredControl(profile: Profile | undefined): {
    [key: string]: any;
  } {
    return profile?.isCorporate === true ? [''] : ['', Validators.required];
  }

  private getPostPayload(emailPayload = false): Observable<CorporateContactUs> {
    const form = this.reactiveForm;
    const topicKey = form.get('topic').value;
    return this.profile$.pipe(
      map((profile: Profile) => {
        return {
          subject: this.subjectMap[topicKey],
          message: form.get('message').value,
          reason: this.reasonMap[topicKey],
          ...(!emailPayload && { attachmentIds: this.mapAttachmentIds(this.fileUploader?.attachments) }),
          ...((emailPayload || !profile) && {
            companyName: form.get('companyName').value,
            customerNumber: form.get('customerNumber').value,
            country: form.get('country').value,
            salutation: form.get('salutation').value,
            firstname: form.get('firstName').value,
            lastname: form.get('familyName').value,
            phone: parsePhoneNumber(form.get('phonePrefix').value, form.get('phoneNumber').value),
            email: form.get('email').value,
          }),
        };
      })
    );
  }

  private mapAttachmentIds(attachments: Attachment[] = []): string[] {
    return attachments
      .filter((attachment: Attachment): boolean => attachment.status === AttachmentStatus.READY)
      .map((attachment: Attachment): string => attachment.fileId);
  }

  private setInputOptions(): void {
    this.countries$ = this.corporateService.getCountryInputOptions();
    this.salutations$ = this.getSalutationOptions();
    this.phonePrefixes$ = this.getPhonePrefixes();
  }

  private prefill(): void {
    this.subscriptions.add(
      this.profile$.pipe(withLatestFrom(this.countryCodes$)).subscribe(([profile, countryCodes]) => {
        this.reactiveForm = this.fb.group({
          topic: [this.topics[0], Validators.required],
          message: ['', [Validators.required, Validators.maxLength(1000)]],
          attachments: [],
          companyName: this.getRequiredControl(profile),
          country: this.getRequiredControl(profile),
          salutation: [''],
          firstName: this.getNameControl(profile),
          familyName: this.getNameControl(profile),
          email: this.getEmailControl(profile),
          phonePrefix: this.getRequiredControl(profile),
          phoneNumber: this.getRequiredControl(profile),
          customerNumber: ['', Validators.maxLength(8)],
        });

        this.reactiveForm.get('companyName').setValue(profile?.corporate?.name);
        this.reactiveForm.get('country').setValue(profile?.corporate?.country);
        if (profile?.corporate?.country) {
          this.countryChanged(profile.corporate.country, countryCodes);
        }
        this.reactiveForm.get('firstName').setValue(profile?.firstname);
        this.reactiveForm.get('familyName').setValue(profile?.lastname);
        this.reactiveForm.get('email').setValue(profile?.email);
        this.reactiveForm
          .get('phoneNumber')
          .setValue(
            profile?.parsedMobilePhone?.nationalNumber
              ? String(profile?.parsedMobilePhone?.nationalNumber)
              : this.stripPhoneNumber(profile?.mobilePhone)
          );
        this.reactiveForm.get('customerNumber').setValue(profile?.corporate?.clientId);
      })
    );
  }
  private getEmailControl(profile: Profile | undefined): {
    [key: string]: any;
  } {
    return profile?.isCorporate === true
      ? ['', Validators.pattern(EMAIL_REGEX)]
      : ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]];
  }
  private getNameControl(profile: Profile | undefined): {
    [key: string]: any;
  } {
    return profile?.isCorporate === true
      ? ['', Validators.pattern(NAME_PATTERN)]
      : ['', [Validators.required, Validators.pattern(NAME_PATTERN)]];
  }

  private getSalutationOptions(): Observable<SelectOption[]> {
    return this.languageService
      .translate('SERVICE_FORMS.serviceForms.corporateContactForm.contactDetails.salutation.options')
      .pipe(
        map((translations: { [key: string]: string }) =>
          Object.entries(translations)
            .filter(([_key, translation]) => Boolean(translation))
            .map(([key, val]) => ({
              name: val,
              value: key,
            }))
        )
      );
  }

  private getPhonePrefixes(): Observable<SelectOption[]> {
    return this.countryCodes$.pipe(
      map((countryCodes: TranslatedCountryCode[]) => {
        return sortBy(
          countryCodes.map((cc) => ({
            name: `${cc.name} (+${cc.phonePrefix})`,
            value: `${cc.countryCode}|${cc.phonePrefix}`,
          })),
          (option) => option.name
        );
      })
    );
  }
}
